
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Target, Zap, BarChart3, Sparkles, DollarSign, Users, BrainCircuit } from 'lucide-react';

const AIAdManager: React.FC = () => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const handleGenerateAds = () => {
    setIsGenerating(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsGenerating(false);
      
      toast({
        title: "Ad campaigns generated!",
        description: "AI has created optimized ad campaigns based on your music and audience."
      });
    }, 1500);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">AI Ad Manager</h2>
          <p className="text-muted-foreground">Create hyper-targeted ads optimized for your audience</p>
        </div>
        <Button 
          onClick={handleGenerateAds} 
          disabled={isGenerating}
          variant="outline"
        >
          {isGenerating ? "Generating..." : "Generate New Ads"}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-background/70">
            <CardHeader className="pb-2">
              <CardTitle>Ad Campaign Builder</CardTitle>
              <CardDescription>Create AI-optimized ad campaigns for different platforms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-background/80">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30">
                        Streaming Growth
                      </Badge>
                      <Target className="h-5 w-5 text-blue-500" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h3 className="font-semibold mb-2">Spotify Growth Campaign</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Targeted ads to increase your Spotify monthly listeners and followers.
                    </p>
                    <Button variant="outline" className="w-full">Create Campaign</Button>
                  </CardContent>
                </Card>
                
                <Card className="bg-background/80">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/30">
                        Content Promo
                      </Badge>
                      <Sparkles className="h-5 w-5 text-purple-500" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h3 className="font-semibold mb-2">New Release Promo</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Multi-platform campaign to promote your latest single or album.
                    </p>
                    <Button variant="outline" className="w-full">Create Campaign</Button>
                  </CardContent>
                </Card>
                
                <Card className="bg-background/80">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                        Ticket Sales
                      </Badge>
                      <DollarSign className="h-5 w-5 text-amber-500" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h3 className="font-semibold mb-2">Concert Ticket Ads</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Geo-targeted ads to drive ticket sales for your upcoming shows.
                    </p>
                    <Button variant="outline" className="w-full">Create Campaign</Button>
                  </CardContent>
                </Card>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <h3 className="font-semibold">Custom AI Ad Creation</h3>
                <div className="p-6 border border-dashed rounded-lg flex flex-col items-center justify-center text-center">
                  <BrainCircuit className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-4">
                    Let AI analyze your audience and create optimized ads for your specific goals
                  </p>
                  <Button>Start Custom Campaign</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ad Performance</CardTitle>
              <CardDescription>How your ads are performing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-md">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <Badge variant="outline" className="mb-1 bg-green-500/10 text-green-500 border-green-500/30">Active</Badge>
                    <h3 className="font-medium">Summer Tour Promo</h3>
                  </div>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Budget spent: $42/$100</span>
                  <span>CTR: 2.8%</span>
                </div>
                <div className="w-full bg-muted/50 rounded-full h-2 mt-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '48%' }}></div>
                </div>
              </div>
              
              <div className="p-4 bg-muted/30 rounded-md">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <Badge variant="outline" className="mb-1 bg-blue-500/10 text-blue-500 border-blue-500/30">Active</Badge>
                    <h3 className="font-medium">Spotify Growth Campaign</h3>
                  </div>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Budget spent: $78/$150</span>
                  <span>CTR: 3.2%</span>
                </div>
                <div className="w-full bg-muted/50 rounded-full h-2 mt-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '52%' }}></div>
                </div>
              </div>
              
              <Button variant="outline" className="w-full">View All Campaigns</Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Audience Insights</CardTitle>
              <CardDescription>AI-generated targeting recommendations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium flex items-center gap-1.5 mb-2">
                  <Zap className="h-4 w-4 text-yellow-400" />
                  Best Performing Audience
                </h3>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Badge variant="outline" className="bg-background/30">18-24 age group</Badge>
                  <Badge variant="outline" className="bg-background/30">Urban locations</Badge>
                  <Badge variant="outline" className="bg-background/30">Weekend browsers</Badge>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-medium flex items-center gap-1.5 mb-2">
                  <Users className="h-4 w-4 text-purple-500" />
                  Interest Targeting
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="bg-background/30">Alternative rock</Badge>
                  <Badge variant="outline" className="bg-background/30">Live music events</Badge>
                  <Badge variant="outline" className="bg-background/30">Indie culture</Badge>
                  <Badge variant="outline" className="bg-background/30">Music production</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIAdManager;
